// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Hook for decision tree computation via R backend
 * Uses TanStack Query for caching, loading states, and request deduplication
 */

import { useMutation } from "@tanstack/react-query";
import { useVaccineClientStore } from "../store/vaccineClientStore";
import { useVaccineDashboardStore } from "../store/vaccineDashboardStore";
import {
  computeDecisionTree,
  type DecisionTreeResponse,
  DecisionTreeApiError,
  type DecisionTreeRequest,
  type IncrementalResults,
  type Metrics,
} from "../services/decisionTreeService";
import type { VaccineOutputs } from "../components/VaccineDashboard/lib/models";
import { useAlerts } from "./useAlerts";
import {
  validateQalys,
  validateBudgetImpact,
  combineValidationResults,
  type ValidationResult,
  type ValidationContext,
} from "../lib/outputValidation";

/**
 * Result type including validation
 */
export interface VaccineOutputsWithValidation {
  model: VaccineOutputs;
  validation: {
    overall: ValidationResult;
    qaly: ValidationResult;
    budgetImpact: ValidationResult;
  };
}

/**
 * Convert backend DecisionTreeResponse to VaccineOutputs format with validation
 *
 * @note This function converts raw backend types (with potential arrays) to clean domain types (always scalar).
 * Arrays are treated as data quality issues and logged, not normalized as "this is fine".
 *
 * @note This function may return NaN values for invalid backend data.
 * The validation layer checks for NaN and unrealistic values, returning warnings/errors.
 *
 * @note All toNumber() calls include field names for meaningful error logging.
 * Check console for [Data Mapping] errors/warnings when debugging backend data issues.
 *
 * @param response - Backend response to map (uses raw types)
 * @param context - Optional context (population, timeHorizon) for validation
 * @returns VaccineOutputsWithValidation including model data and validation results
 */
export function mapBackendResponseToVaccineOutputs(
  response: DecisionTreeResponse,
  context: ValidationContext = {}
): VaccineOutputsWithValidation {
  // Guard: Check for empty or null response
  if (!response) {
    throw new Error("[Decision Tree] Empty backend response");
  }

  // Guard: Check for error response
  if ((response as any).error) {
    const errorMsg = (response as any).error.message || "unknown error";
    throw new Error(`[Decision Tree] Backend error: ${errorMsg}`);
  }

  // Guard: Check for required fields
  if (!response.incremental) {
    console.error(
      '[Decision Tree] Backend response missing "incremental" block',
      response
    );
    throw new Error(
      '[Decision Tree] Backend response missing "incremental" block'
    );
  }

  if (!response.metrics) {
    console.error(
      '[Decision Tree] Backend response missing "metrics" block',
      response
    );
    throw new Error('[Decision Tree] Backend response missing "metrics" block');
  }

  if (!response.results) {
    console.error(
      '[Decision Tree] Backend response missing "results" block',
      response
    );
    throw new Error('[Decision Tree] Backend response missing "results" block');
  }

  // Debug: Log response structure for troubleshooting
  console.debug("[Decision Tree] Raw backend response structure:", {
    hasResults: !!response.results,
    hasIncremental: !!response.incremental,
    hasMetrics: !!response.metrics,
    responseKeys: Object.keys(response),
  });

  const { results } = response;

  /**
   * Helper to convert array or scalar to number
   *
   * @param val - Value to convert (unknown type for type safety)
   * @param fieldName - Field name for error logging (required, never optional)
   * @returns number - Returns NaN for invalid values instead of silently defaulting to 0
   *
   * @note Callers MUST handle NaN values:
   *   - Use Number.isNaN() to check for invalid values
   *   - Validation layer should check for NaN and display warnings
   *   - UI should show "invalid result" state instead of displaying NaN or 0
   *
   * @note For critical KPIs (QALYs, budget impact, costs), arrays are treated as errors (returns NaN)
   * @note For non-critical fields, arrays use first element as workaround (with warning)
   */
  const toNumber = (val: unknown, fieldName: string): number => {
    // Handle numbers (including NaN and Infinity)
    if (typeof val === "number") {
      if (isNaN(val)) {
        console.error(`[Data Mapping] NaN received for ${fieldName}:`, val);
        return NaN;
      }
      return val;
    }

    // Handle arrays
    // For critical KPIs (QALYs, budget impact, costs), arrays are unexpected and should be treated as errors
    // For other fields, we may use first element as a workaround but log clearly
    if (Array.isArray(val)) {
      if (val.length === 0) {
        console.error(`[Data Mapping] Empty array for ${fieldName}:`, val);
        return NaN;
      }

      // Critical KPIs where arrays are definitely unexpected - treat as error
      const isCriticalKPI =
        fieldName.includes("delta_qalys") ||
        fieldName.includes("budget_impact") ||
        fieldName.includes("total_cost") ||
        fieldName.includes("programme_cost") ||
        fieldName.includes("healthcare_cost");

      if (isCriticalKPI) {
        console.error(
          `[Data Mapping] Unexpected array for critical KPI ${fieldName} (expected scalar), treating as invalid:`,
          val
        );
        return NaN; // Don't guess - treat as invalid
      }

      // For non-critical fields, use first element but log clearly
      if (val.length > 1) {
        console.warn(
          `[Data Mapping] Array with ${val.length} elements encountered for ${fieldName}, using first element as workaround:`,
          val
        );
      } else {
        console.warn(
          `[Data Mapping] Array encountered for ${fieldName} (expected scalar), using first element as workaround:`,
          val
        );
      }
      // Recursively call toNumber on first element with updated field name
      return toNumber(val[0], `${fieldName}[0]`);
    }

    // Handle null/undefined
    if (val === null || val === undefined) {
      console.error(
        `[Data Mapping] Null or undefined value for ${fieldName}:`,
        val
      );
      return NaN;
    }

    // Attempt to parse string or other types
    const parsed = parseFloat(String(val));
    if (isNaN(parsed)) {
      console.error(
        `[Data Mapping] Failed to parse ${fieldName}:`,
        val,
        `(type: ${typeof val})`
      );
      return NaN;
    }

    return parsed;
  };

  /**
   * Convert raw incremental results to clean domain types
   *
   * Converts RawIncrementalResults → IncrementalResults, handling arrays as data quality issues.
   */
  const convertRawIncrementalToDomain = (
    rawIncremental: DecisionTreeResponse["incremental"]
  ): IncrementalResults => {
    if (!rawIncremental) {
      throw new Error(
        '[Decision Tree] Backend response missing "incremental" block'
      );
    }
    return {
      delta_qalys: toNumber(
        rawIncremental.delta_qalys,
        "incremental.delta_qalys"
      ),
      delta_life_years: toNumber(
        rawIncremental.delta_life_years,
        "incremental.delta_life_years"
      ),
      delta_cost: toNumber(rawIncremental.delta_cost, "incremental.delta_cost"),
      deaths_averted: toNumber(
        rawIncremental.deaths_averted,
        "incremental.deaths_averted"
      ),
      cases_avoided: toNumber(
        rawIncremental.cases_avoided,
        "incremental.cases_avoided"
      ),
      hospitalizations_avoided: toNumber(
        rawIncremental.hospitalizations_avoided,
        "incremental.hospitalizations_avoided"
      ),
      events_prevented: toNumber(
        rawIncremental.events_prevented,
        "incremental.events_prevented"
      ),
    };
  };

  /**
   * Convert raw metrics to clean domain types
   *
   * Converts RawMetrics → Metrics, handling arrays as data quality issues.
   */
  const convertRawMetricsToDomain = (
    rawMetrics: DecisionTreeResponse["metrics"]
  ): Metrics => {
    if (!rawMetrics) {
      throw new Error(
        '[Decision Tree] Backend response missing "metrics" block'
      );
    }
    return {
      icer: toNumber(rawMetrics.icer, "metrics.icer"),
      bcr: toNumber(rawMetrics.bcr, "metrics.bcr"),
      budget_impact: toNumber(
        rawMetrics.budget_impact,
        "metrics.budget_impact"
      ),
    };
  };

  // Convert raw types to clean domain types
  const incremental: IncrementalResults = convertRawIncrementalToDomain(
    response.incremental
  );
  const metrics: Metrics = convertRawMetricsToDomain(response.metrics);

  const toString = (val: any): string => {
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return String(val[0] || "");
    return String(val || "");
  };

  // Generate time series data for timeline chart
  // Since backend doesn't provide time-series, create a simple projection
  const vacCases = toNumber(results.vaccinate.cases, "results.vaccinate.cases");
  const noVacCases = toNumber(
    results.no_vaccinate.cases,
    "results.no_vaccinate.cases"
  );
  const vacCost = toNumber(
    results.vaccinate.total_cost,
    "results.vaccinate.total_cost"
  );
  const noVacCost = toNumber(
    results.no_vaccinate.total_cost,
    "results.no_vaccinate.total_cost"
  );
  const avoidedCases = noVacCases - vacCases;
  const savings = noVacCost - vacCost;

  const series =
    results.series && results.series.length > 0
      ? results.series.map((s) => {
          // Debug: Log available fields to verify healthcare_cost_novac is present
          if (process.env.NODE_ENV === 'development') {
            const hasNovac = 'healthcare_cost_novac' in s;
            const hasVac = 'healthcare_cost_vac' in s;
            if (!hasNovac || !hasVac) {
              console.log('[DEBUG] Series entry fields:', Object.keys(s));
              console.log('[DEBUG] healthcare_cost_novac present:', hasNovac, 'value:', s.healthcare_cost_novac);
              console.log('[DEBUG] healthcare_cost_vac present:', hasVac, 'value:', s.healthcare_cost_vac);
            }
          }
          
          return {
            year: String(s.year),
            // Use backend data when available (multi-year), fallback to placeholders for backward compatibility
            baselineCases: s.baselineCases !== undefined ? s.baselineCases : noVacCases,
            withVaccineCases: s.withVaccineCases !== undefined ? s.withVaccineCases : vacCases,
            avoidedCases: 0,
            savingsEUR: s.healthcare_savings,
            programmeCost: s.programme_cost,
            netCost: s.net_impact,
            cumulativeNetCost: s.cumulative_net_impact,
            // Multi-year analysis fields (mapped from backend camelCase fields)
            vaccineEfficacy: s.vaccineEfficacy,
            qalysWithVaccine: s.qalysWithVaccine,
            qalysWithoutVaccine: s.qalysWithoutVaccine,
            qalysWithVaccineDiscounted: s.qalysWithVaccineDiscounted,
            qalysWithoutVaccineDiscounted: s.qalysWithoutVaccineDiscounted,
            discountFactor: s.discountFactor,
            aliveVac: s.aliveVac,
            aliveNovac: s.aliveNovac,
            backgroundMortality: s.backgroundMortality,
            backgroundUtility: s.backgroundUtility,
            // Use actual healthcare costs from backend (accounts for mortality)
            // These are the actual healthcare costs per year, already accounting for population changes
            costWithoutVaccine: s.healthcare_cost_novac,
            costWithVaccine: s.healthcare_cost_vac !== undefined && s.healthcare_cost_novac !== undefined
              ? s.programme_cost + s.healthcare_cost_vac
              : undefined,
            bgDeathsNovac: s.bgDeathsNovac,
            bgDeathsVac: s.bgDeathsVac,
            deathsNovac: s.deathsNovac,
            deathsVac: s.deathsVac,
          };
        })
      : [
          {
            year: "0",
            baselineCases: noVacCases,
            withVaccineCases: noVacCases,
            avoidedCases: 0,
            savingsEUR: 0,
            programmeCost: 0,
            netCost: 0,
            cumulativeNetCost: 0,
          },
          {
            year: "1",
            baselineCases: noVacCases,
            withVaccineCases: vacCases,
            avoidedCases,
            savingsEUR: savings,
            programmeCost: toNumber(
              results.vaccinate.programme_cost,
              "results.vaccinate.programme_cost"
            ),
            netCost:
              toNumber(
                results.vaccinate.programme_cost,
                "results.vaccinate.programme_cost"
              ) - savings,
            cumulativeNetCost:
              toNumber(
                results.vaccinate.programme_cost,
                "results.vaccinate.programme_cost"
              ) - savings,
          },
        ];

  // Generate pie chart data for cost breakdown
  const programmeCost = toNumber(
    results.vaccinate.programme_cost,
    "results.vaccinate.programme_cost"
  );
  const healthcareCost = toNumber(
    results.vaccinate.healthcare_cost,
    "results.vaccinate.healthcare_cost"
  );
  const pieData = [
    { name: "Programme Cost", value: programmeCost },
    { name: "Healthcare Cost", value: healthcareCost },
  ];

  const outputs: VaccineOutputs = {
    // Vaccinate strategy outputs
    vaccinate: {
      lifeYears: toNumber(
        results.vaccinate.life_years,
        "results.vaccinate.life_years"
      ),
      qalys: toNumber(results.vaccinate.qalys, "results.vaccinate.qalys"),
      deaths: toNumber(results.vaccinate.deaths, "results.vaccinate.deaths"),
      cases: toNumber(results.vaccinate.cases, "results.vaccinate.cases"),
      hospitalizations: toNumber(
        results.vaccinate.hospitalizations,
        "results.vaccinate.hospitalizations"
      ),
      programmeCost: toNumber(
        results.vaccinate.programme_cost,
        "results.vaccinate.programme_cost"
      ),
      healthcareCost: toNumber(
        results.vaccinate.healthcare_cost,
        "results.vaccinate.healthcare_cost"
      ),
      totalCost: toNumber(
        results.vaccinate.total_cost,
        "results.vaccinate.total_cost"
      ),
      indirectCost: toNumber(
        results.vaccinate.indirect_cost,
        "results.vaccinate.indirect_cost"
      ),
    },

    // No vaccinate strategy outputs
    noVaccinate: {
      lifeYears: toNumber(
        results.no_vaccinate.life_years,
        "results.no_vaccinate.life_years"
      ),
      qalys: toNumber(results.no_vaccinate.qalys, "results.no_vaccinate.qalys"),
      deaths: toNumber(
        results.no_vaccinate.deaths,
        "results.no_vaccinate.deaths"
      ),
      cases: toNumber(results.no_vaccinate.cases, "results.no_vaccinate.cases"),
      hospitalizations: toNumber(
        results.no_vaccinate.hospitalizations,
        "results.no_vaccinate.hospitalizations"
      ),
      programmeCost: toNumber(
        results.no_vaccinate.programme_cost,
        "results.no_vaccinate.programme_cost"
      ),
      healthcareCost: toNumber(
        results.no_vaccinate.healthcare_cost,
        "results.no_vaccinate.healthcare_cost"
      ),
      totalCost: toNumber(
        results.no_vaccinate.total_cost,
        "results.no_vaccinate.total_cost"
      ),
      indirectCost: toNumber(
        results.no_vaccinate.indirect_cost,
        "results.no_vaccinate.indirect_cost"
      ),
    },

    // Incremental outcomes (Vaccinate - No Vaccinate)
    incremental: {
      lifeYearsGained: toNumber(
        incremental.delta_life_years,
        "incremental.delta_life_years"
      ),
      qalyGained: toNumber(incremental.delta_qalys, "incremental.delta_qalys"),
      deathsAverted: toNumber(
        incremental.deaths_averted,
        "incremental.deaths_averted"
      ),
      casesAvoided: Math.round(toNumber(
        incremental.cases_avoided,
        "incremental.cases_avoided"
      )),
      hospitalizationsAvoided: toNumber(
        incremental.hospitalizations_avoided,
        "incremental.hospitalizations_avoided"
      ),
      costDifference: toNumber(
        incremental.delta_cost,
        "incremental.delta_cost"
      ),
    },

    // Economic metrics
    icer: toNumber(metrics.icer, "metrics.icer"),
    bcr: toNumber(metrics.bcr, "metrics.bcr"),
    budgetImpact: toNumber(metrics.budget_impact, "metrics.budget_impact"),

    costBreakdown: {
      directMedicalCost: healthcareCost,
      programmeDeliveryCost:
        toNumber(
          results.waterfall_data?.programme_cost,
          "results.waterfall_data.programme_cost"
        ) || programmeCost,
      productivityCost: toNumber(
        results.vaccinate.indirect_cost,
        "results.vaccinate.indirect_cost"
      ),
      adverseEventCost: toNumber(
        results.waterfall_data?.adverse_event_cost,
        "results.waterfall_data.adverse_event_cost"
      ),
    },

    avoidedCases: toNumber(
      incremental.cases_avoided,
      "incremental.cases_avoided"
    ),
    avoidedHosp: toNumber(
      incremental.hospitalizations_avoided,
      "incremental.hospitalizations_avoided"
    ),
    qalyGained: toNumber(incremental.delta_qalys, "incremental.delta_qalys"),
    totalSavings: -toNumber(incremental.delta_cost, "incremental.delta_cost"), // Negative cost difference = savings
    netSavings: -toNumber(incremental.delta_cost, "incremental.delta_cost"),
    vaccineCosts: toNumber(
      results.vaccinate.programme_cost,
      "results.vaccinate.programme_cost"
    ),
    casesCostSaved: -toNumber(incremental.delta_cost, "incremental.delta_cost"),
    productivitySaved: toNumber(
      results.waterfall_data?.productivity_savings,
      "results.waterfall_data.productivity_savings"
    ),
    doses: 0, // Not provided by backend yet
    series, // Generated time-series data
    pieData, // Generated pie chart data
    regional: results.regional_breakdown
      ? results.regional_breakdown.map((r, idx) => ({
          id: toString(r.id),
          name: toString(r.name),
          population: toNumber(
            r.population,
            `results.regional_breakdown[${idx}].population`
          ),
          coverage:
            toNumber(
              r.coverage,
              `results.regional_breakdown[${idx}].coverage`
            ) * 100,
          casesPrevented: toNumber(
            r.cases_prevented,
            `results.regional_breakdown[${idx}].cases_prevented`
          ),
          hospitalizationsPrevented: toNumber(
            r.hospitalizations_prevented,
            `results.regional_breakdown[${idx}].hospitalizations_prevented`
          ),
          lifeYearsGained: toNumber(
            r.life_years_gained,
            `results.regional_breakdown[${idx}].life_years_gained`
          ),
          costImpact: toNumber(
            r.cost_impact,
            `results.regional_breakdown[${idx}].cost_impact`
          ),
        }))
      : [],
    waterfall: {
      treatmentSavings: toNumber(
        results.waterfall_data?.treatment_savings,
        "results.waterfall_data.treatment_savings"
      ),
      productivitySavings: toNumber(
        results.waterfall_data?.productivity_savings,
        "results.waterfall_data.productivity_savings"
      ),
      adverseEventCost: toNumber(
        results.waterfall_data?.adverse_event_cost,
        "results.waterfall_data.adverse_event_cost"
      ),
      programmeCost: toNumber(
        results.waterfall_data?.programme_cost,
        "results.waterfall_data.programme_cost"
      ),
    },
    // Multi-year analysis output
    veByYear: response.veByYear, // VE values for years 1..H (undefined for single-year)
  };

  // Run validation on key metrics
  const qalyValue = outputs.incremental.qalyGained;
  const budgetImpactValue = outputs.budgetImpact;

  const qalyValidation = validateQalys(qalyValue, context);
  const budgetImpactValidation = validateBudgetImpact(
    budgetImpactValue,
    context
  );
  const overallValidation = combineValidationResults(
    qalyValidation,
    budgetImpactValidation
  );

  // Log validation issues for monitoring
  if (overallValidation.severity !== "ok") {
    console.warn("[Output Validation] Validation issues detected:", {
      overallSeverity: overallValidation.severity,
      issues: overallValidation.issues.map((i) => ({
        field: i.field,
        code: i.code,
        severity: i.severity,
        message: i.message,
      })),
      context,
    });
  }

  return {
    model: outputs,
    validation: {
      overall: overallValidation,
      qaly: qalyValidation,
      budgetImpact: budgetImpactValidation,
    },
  };
}

export interface ComputationResult {
  response: DecisionTreeResponse;
  computedAt: number;
  vaccineOutputs: VaccineOutputs;
  validation: VaccineOutputsWithValidation["validation"];
}

export function useDecisionTreeComputation() {
  const getTreeStateForAPI = useVaccineClientStore(
    (state) => state.getTreeStateForAPI
  );
  const nodes = useVaccineClientStore((state) => state.nodes);
  const costParameters = useVaccineClientStore((state) => state.costParameters);
  const populationParameters = useVaccineClientStore(
    (state) => state.populationParameters
  );
  const simulationParameters = useVaccineClientStore(
    (state) => state.simulationParameters
  );
  const modelMetadata = useVaccineClientStore((state) => state.modelMetadata);

  const setBackendResults = useVaccineDashboardStore(
    (state) => state.setBackendResults
  );

  const { showError } = useAlerts();

  // TanStack Query mutation for computation
  const mutation = useMutation<ComputationResult, Error, void>({
    mutationKey: ["decision-tree-compute"],
    mutationFn: async () => {
      // Validate we have required data
      if (Object.keys(useVaccineClientStore.getState().nodes).length === 0) {
        throw new Error("Decision tree is empty. Add nodes before computing.");
      }

      // Get tree state from store in API format
      const treeState = getTreeStateForAPI();

      // Debug: Log tree structure
      console.log("[Decision Tree] Tree structure being sent:", {
        nodeCount: treeState.nodes?.length || 0,
        edgeCount: treeState.edges?.length || 0,
        rootId: treeState.rootId,
        sampleNode: treeState.nodes?.[0],
        sampleEdge: treeState.edges?.[0],
        nodesWithNodeType: treeState.nodes
          ?.filter((n) => n.nodeType)
          .map((n) => ({ id: n.id, label: n.label, nodeType: n.nodeType })),
      });

      // Prepare request
      const request: DecisionTreeRequest = {
        tree: treeState,
        parameters: {
          model_metadata: modelMetadata || {
            model_name: "Vaccine Decision Tree",
            currency: "EUR",
            perspective: "healthcare",
          },
          cost_parameters: costParameters || {
            cost_vaccine_dose: 15,
            cost_admin: 5,
            cost_infection: 200,
          },
          population_parameters: populationParameters || {
            population_size: 5637000,
            coverage_rate: 0.7,
            baseline_incidence: 0.05,
            baseline_mortality: 0.001,
            utility_healthy: 1.0,
            utility_infection: 0.9,
            duration_infection: 0.1,
          },
          simulation_parameters: simulationParameters || {
            cycles: 1,
            willingness_to_pay: 20000,
            discount_rate: 0.03,
          },
        },
      };

      // Call backend
      const response = await computeDecisionTree(request);
      const computedAt = Date.now();

      // Prepare validation context
      const validationContext: ValidationContext = {
        population: populationParameters?.population_size,
        timeHorizon: simulationParameters?.cycles,
      };

      // Convert results with validation
      const { model: vaccineOutputs, validation } =
        mapBackendResponseToVaccineOutputs(response, validationContext);

      // Store results in dashboard store (including validation)
      setBackendResults(vaccineOutputs, computedAt, validation);

      console.log("[Decision Tree] Computation successful:", {
        requestId: response.request_id,
        elapsedMs: response.elapsed_ms,
        icer: vaccineOutputs.icer,
        bcr: vaccineOutputs.bcr,
      });

      return { response, computedAt, vaccineOutputs, validation };
    },
    onError: (error: Error) => {
      let errorMessage = error.message;
      let errorTitle = "Computation Failed";

      if (error instanceof DecisionTreeApiError) {
        errorMessage = error.message;

        if (
          typeof error.code === "string" &&
          error.code.includes("VALIDATION")
        ) {
          errorTitle = "Validation Error";
        } else if (
          typeof error.code === "string" &&
          error.code.includes("TREE")
        ) {
          errorTitle = "Tree Structure Error";
        } else if (
          errorMessage.includes("callr subprocess") ||
          errorMessage.includes("defunct")
        ) {
          errorTitle = "Backend Computation Error";
          errorMessage =
            "The R computation engine encountered an error. Please check your decision tree structure and parameters.";
        }

        console.error("[Decision Tree] API Error:", {
          code: error.code,
          message: error.message,
          requestId: error.requestId,
          status: error.status,
        });
      } else {
        console.error("[Decision Tree] Computation error:", error);
      }

      showError(errorMessage, errorTitle);
    },
  });

  // Format error message
  const getErrorMessage = (): string | null => {
    if (!mutation.error) return null;

    const error = mutation.error;

    if (error instanceof DecisionTreeApiError) {
      return `${error.code}: ${error.message}`;
    } else if (error instanceof Error) {
      if (error.name === "AbortError") {
        return "Computation timed out (>30s). Try simplifying the tree.";
      } else if (error.message.includes("Failed to fetch")) {
        return "Backend is not running. Please start the R backend server.";
      } else {
        return error.message;
      }
    }

    return "Unknown error occurred";
  };

  return {
    // TanStack Query states
    loading: mutation.isPending,
    error: getErrorMessage(),
    results: mutation.data?.response ?? null,
    lastComputedAt: mutation.data?.computedAt ?? null,

    // Actions
    compute: () => mutation.mutate(),
    clearError: () => mutation.reset(),
    reset: () => mutation.reset(),

    // Computed flags
    hasResults: mutation.isSuccess && mutation.data !== undefined,
    hasError: mutation.isError,
    canCompute: Object.keys(nodes).length > 0 && !mutation.isPending,
  };
}
